import { useCallback, useEffect, useMemo, useRef } from 'react';

import { ChatType, useChats } from '@/store/chats';
import {
  useGetConnieSessions,
  useGetContactSessions,
  useGetDocumentSessions,
  useGetQuoteSessions,
} from '@/store/sessions';
import { Session } from '@/types/chat';

const CONNIE_TITLE_REFRESH_MS = 1500;

type SupportedChatType =
  | typeof ChatType.job
  | typeof ChatType.connie
  | typeof ChatType.document
  | typeof ChatType.quote
  | typeof ChatType.contact;

interface Props {
  chatType: SupportedChatType;
  entityId?: string;
  userId?: string;
  enabled?: boolean;
}

// Unified sessions list/query logic for all chat types.
export const useChatSessions = ({
  chatType,
  entityId,
  userId,
  enabled = true,
}: Props) => {
  const sessionId = useChats(state => state.chatState[chatType].sessionId);
  const updateChatState = useChats(state => state.updateChatState);
  const contactId = useChats(state =>
    chatType === ChatType.contact
      ? state.chatState.contact.contactId
      : state.chatState.job.contactId
  );
  const isDocumentChat = chatType === ChatType.document;
  const isQuoteChat = chatType === ChatType.quote;
  const isGlobalConnieChat = chatType === ChatType.connie;
  const isJobChat = chatType === ChatType.job;
  const usesConnieSessions = isGlobalConnieChat || isJobChat;
  const isContactBasedChat = chatType === ChatType.contact;
  const jobContactId = isJobChat ? (contactId?.toString() ?? '') : undefined;

  const documentSessionsQuery = useGetDocumentSessions(
    { docId: entityId ?? '' },
    { enabled: isDocumentChat && !!entityId && enabled }
  );

  const quoteSessionsQuery = useGetQuoteSessions(
    { quoteId: entityId ?? '' },
    { enabled: isQuoteChat && !!entityId && enabled }
  );

  const contactSessionsQuery = useGetContactSessions(
    { contactId: contactId?.toString() ?? '' },
    { enabled: isContactBasedChat && !!contactId && enabled }
  );

  const connieSessionsQuery = useGetConnieSessions(
    {
      userId: userId ?? '',
      contactId: jobContactId,
    },
    {
      enabled:
        usesConnieSessions &&
        !!userId &&
        enabled &&
        (isGlobalConnieChat || !!jobContactId),
    }
  );

  const sessionsQuery = isDocumentChat
    ? documentSessionsQuery
    : isQuoteChat
      ? quoteSessionsQuery
      : usesConnieSessions
        ? connieSessionsQuery
        : contactSessionsQuery;
  const { refetch: refetchSessions } = sessionsQuery;

  // Keep an empty fallback array stable between renders
  const sessions = useMemo(
    () => sessionsQuery.data ?? [],
    [sessionsQuery.data]
  );

  // LLM titles lag ~1–3s after the first turn; one short refetch covers the gap.
  const hasPendingConnieTitles = useMemo(
    () => sessions.some(session => session.titlePending),
    [sessions]
  );
  const connieTitleRefreshAttempted = useRef(false);
  useEffect(() => {
    if (!usesConnieSessions || !enabled) {
      connieTitleRefreshAttempted.current = false;
      return;
    }

    if (!hasPendingConnieTitles) {
      connieTitleRefreshAttempted.current = false;
      return;
    }

    if (connieTitleRefreshAttempted.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      connieTitleRefreshAttempted.current = true;
      void refetchSessions();
    }, CONNIE_TITLE_REFRESH_MS);

    return () => window.clearTimeout(timer);
  }, [
    enabled,
    hasPendingConnieTitles,
    usesConnieSessions,
    refetchSessions,
  ]);

  const currentSession = useMemo(
    () =>
      sessionId
        ? (sessions.find(session => session.id === sessionId) ?? null)
        : null,
    [sessionId, sessions]
  );

  // Switch current session (or reset when null).
  const setCurrentSession = useCallback(
    (session: Session | null) => {
      updateChatState(chatType, {
        sessionId: session?.id ?? '',
        isNewSession: !session,
      });
    },
    [updateChatState, chatType]
  );

  const startNewChat = useCallback(() => {
    updateChatState(chatType, { sessionId: '', isNewSession: true });
  }, [updateChatState, chatType]);

  // The response session_id is authoritative. Refresh only to update the list.
  const handleAfterMessageSent = useCallback(() => {
    return refetchSessions();
  }, [refetchSessions]);

  return {
    sessions,
    isSessionsLoading: sessionsQuery.isLoading,
    error: sessionsQuery.error,
    sessionId,
    refetchSessions,
    currentSession,
    setCurrentSession,
    startNewChat,
    handleAfterMessageSent,
  };
};
